import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import MainLayout from '../../layouts/MainLayout';
import { Card, Button, Switch, Tag, message, Spin, Typography, Space } from 'antd';
import {
  ArrowRightOutlined,
  HolderOutlined,
  FormOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { ticketsApi } from '../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import { TableSkeleton } from '../../components/SkeletonLoaders';

const { Text } = Typography;

const fieldTypeLabel = {
  text: 'متن',
  textarea: 'چندخطی',
  select: 'لیست',
};

function SortableFieldRow({ field, onStatusChange, statusLoading }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  };
  const active = field.status === 'active';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-4 mb-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm"
    >
      <button
        type="button"
        className="cursor-grab text-gray-400 hover:text-gray-600 p-1 touch-none"
        aria-label="جابه‌جایی"
        {...attributes}
        {...listeners}
      >
        <HolderOutlined className="text-lg" />
      </button>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'CustomFont' }}>
          {field.field_display_name}
        </div>
        <Text type="secondary" className="text-sm block truncate" style={{ fontFamily: 'CustomFont' }}>
          {field.field_name}
        </Text>
      </div>
      <Tag>{fieldTypeLabel[field.field_type] || field.field_type}</Tag>
      <div className="flex items-center gap-2 shrink-0">
        <Text style={{ fontFamily: 'CustomFont' }}>{active ? 'فعال' : 'غیرفعال'}</Text>
        <Switch
          checked={active}
          loading={statusLoading === field.id}
          onChange={(checked) => onStatusChange(field.id, checked ? 'active' : 'inactive')}
        />
      </div>
    </div>
  );
}

const TicketFormSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [fields, setFields] = useState([]);
  const [statusLoadingId, setStatusLoadingId] = useState(null);
  const [sortSaving, setSortSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const sortedFields = useMemo(
    () => [...fields].sort((a, b) => (a.field_order || 0) - (b.field_order || 0)),
    [fields]
  );

  const ids = useMemo(() => sortedFields.map((f) => f.id), [sortedFields]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ticketsApi.getCustomerTicketForm();
      const list = res?.data;
      setFields(Array.isArray(list) ? list : []);
    } catch (error) {
      message.error('خطا در دریافت فرم تیکت مشتری');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleStatusChange = async (fieldId, status) => {
    setStatusLoadingId(fieldId);
    try {
      await ticketsApi.updateCustomerTicketFormField(fieldId, status);
      message.success('وضعیت فیلد بروزرسانی شد');
      setFields((prev) => prev.map((f) => (f.id === fieldId ? { ...f, status } : f)));
    } catch (error) {
      message.error('خطا در بروزرسانی');
    } finally {
      setStatusLoadingId(null);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const newOrder = arrayMove(sortedFields, oldIndex, newIndex);
    const sorted_values = newOrder.map((f) => f.id);
    setFields(newOrder.map((f, i) => ({ ...f, field_order: i + 1 })));

    setSortSaving(true);
    try {
      await ticketsApi.sortCustomerTicketFormFields(sorted_values);
      message.success('ترتیب فیلدها ذخیره شد');
    } catch (error) {
      message.error('خطا در ذخیره ترتیب');
      load();
    } finally {
      setSortSaving(false);
    }
  };

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded-xl bg-sky-50 dark:bg-sky-900/20 flex items-center justify-center flex-shrink-0">
                <FormOutlined className="text-sky-600 dark:text-sky-400 text-xl" />
              </div>
              <div className="min-w-0">
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">فرم تیکت مشتری</h1>
                {/* <p className="text-gray-500 dark:text-gray-400 text-sm m-0">
                  فعال/غیرفعال، ترتیب نمایش فیلدها در فرم عمومی تیکت
                </p> */}
              </div>
            </div>
            <Space wrap>
              <Button icon={<ArrowRightOutlined />} onClick={() => navigate('/tickets')}>
                بازگشت
              </Button>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={load}
                loading={loading}
                className={PANEL_PRIMARY_BTN_CLASS}
              >
                بروزرسانی
              </Button>
            </Space>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50">
            {loading ? (
              <TableSkeleton rows={6} columns={1} />
            ) : (
              <Card className="border border-gray-100 dark:border-gray-700 max-w-4xl">
                <p className="text-gray-600 dark:text-gray-400 mb-4" style={{ fontFamily: 'CustomFont' }}>
                  ردیف‌ها را بکشید تا ترتیب ذخیره شود. سوئیچ برای فعال یا غیرفعال کردن فیلد است.
                </p>
                {sortSaving ? (
                  <div className="mb-2">
                    <Spin size="small" /> <span className="mr-2">در حال ذخیره ترتیب...</span>
                  </div>
                ) : null}
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                    {sortedFields.map((field) => (
                      <SortableFieldRow
                        key={field.id}
                        field={field}
                        onStatusChange={handleStatusChange}
                        statusLoading={statusLoadingId}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
                {sortedFields.length === 0 ? (
                  <div className="text-center text-gray-500 py-12">فیلدی ثبت نشده است</div>
                ) : null}
              </Card>
            )}
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default TicketFormSettings;
